import { ElasticCallout, Results } from "@usereactify/search";
import React, { useMemo, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
// import { ResultsCalloutVideo } from "./ResultsCalloutVideo";

type Props = Parameters<NonNullable<Parameters<typeof Results>[0]["renderResultCardCallout"]>>[0];

type CalloutProps = {
  pagePosition: number;
  document: ElasticCallout;
  callout: ElasticCallout["callout"];
  itemRef: (node?: Element | null) => void;
};
export const ResultsCallout: React.FC<CalloutProps> = ({ itemRef, callout }) => {
  const { isMobile } = useIsMobile();
  const {
    content,
    title,
    link,
    desktopImage,
    mobileImage,
    desktopVideo,
    mobileVideo,
    backgroundColor,
    textColor,
    displayRows,
    displayColumns,
  } = callout;

  const [isLoading, setIsLoading] = useState(true);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const finishedLoading = () => setIsLoading(false);
  const handleShowOverlay = () => setIsOverlayVisible(true);
  const handleHideOverlay = () => setIsOverlayVisible(false);
  const image = useMemo(
    () => (isMobile && mobileImage ? mobileImage : desktopImage || null),
    [desktopImage, isMobile, mobileImage]
  );
  const video = useMemo(
    () => (isMobile && mobileVideo ? mobileVideo : desktopVideo || null),
    [desktopVideo, isMobile, mobileVideo]
  );
  const bgColor = backgroundColor || "#F9F9F9";
  const tColor = textColor || "#000000";
  const fadeInClasses = `w-full transition-opacity duration-500 ${isLoading ? "opacity-0" : "opacity-100"}`;

  let innerContent;
  if (image) {
    innerContent = (
      <div className="grid-product__image-wrap">
        <div className="grid__image-ratio grid__image-ratio--square">
          <img className="lazyautosizes lazyloaded" src={image} alt={title} onLoad={finishedLoading} />
        </div>
        <div style={{ backgroundColor: bgColor, color: tColor }} className="grid-item__meta">
          <h2 className="h5 grid-product__title">{title}</h2>
          <p className="text-l leading-6" style={{ backgroundColor: bgColor, color: tColor }}>
            {content}
          </p>
          <Cta link={link} title={title} classes="product-link label grey-dark" wArrow={true} />
        </div>
      </div>
    );
  } else if (video) {
    innerContent = (
      <div className="grid-product__image-wrap">
        <div className="grid__image-ratio grid__image-ratio--square">
          <video className={fadeInClasses} muted autoPlay loop onLoadedData={finishedLoading}>
            <source src={video} type="video/mp4" />
          </video>
        </div>
        <div style={{ backgroundColor: bgColor, color: tColor }} className="grid-item__meta">
          <h2 className="h5 grid-product__title">{title}</h2>
          <p className="text-l leading-6" style={{ backgroundColor: bgColor, color: tColor }}>
            {content}
          </p>
          <Cta link={link} title={title} classes="product-link label grey-dark" wArrow={true} />
        </div>
      </div>
    );
  } else {
    innerContent = (
      <div style={{ backgroundColor: bgColor, color: tColor }} className="grid-item__meta">
        <h2 className="h5 grid-product__title">{title}</h2>
        <p className="text-l leading-6" style={{ backgroundColor: bgColor, color: tColor }}>
          {content}
        </p>
        <Cta link={link} title={title} classes="product-link label grey-dark" wArrow={true} />
      </div>
    );
  }

  return (
    <div
      ref={itemRef}
      className="grid-item grid-product product-grid-item"
      onMouseEnter={handleShowOverlay}
      onMouseLeave={handleHideOverlay}
    >
      <div className="grid-item__content" style={{ backgroundColor: bgColor, color: tColor }}>
        {innerContent}
      </div>
    </div>
  );
};

type CtaProps = {
  link: string;
  title: string;
  classes?: string;
  wArrow?: boolean;
};

const Cta: React.FC<CtaProps> = ({ link, title, classes, wArrow }) => {
  return link ? (
    <a href={link} title={title} className={classes}>
      View Collection {wArrow && "→"}
    </a>
  ) : null;
};

type OverlayBoxProps = {
  title: string;
  content?: string;
  tColor?: string;
  bgColor?: string;
  isVisible: boolean;
};

const OverlayBox: React.FC<OverlayBoxProps> = (props) => {
  const { isVisible, content, title, tColor = "#000", bgColor = "#f9f9f9" } = props;
  const { isMobile } = useIsMobile();
  const classes = isMobile
    ? `text-left z-10 p-8 duration-300 transition-opacity`
    : `absolute w-full lg:max-w-[50%] lg:w-auto bottom-0 text-right z-10 p-8 duration-500 transition-all ${
        isVisible ? "opacity-100 right-0" : "opacity-0 -right-8"
      }`;

  return (
    <div className={classes} style={{ backgroundColor: bgColor }}>
      <h2 className="text-3xl m-0" style={{ color: tColor }}>
        {title}
      </h2>
      {content && (
        <p className="mt-4" style={{ color: tColor }}>
          {content}
        </p>
      )}
    </div>
  );
};
